import { AppUserProfile, SubcodePartyType } from './for-authz';
import { ProductNameEnum, ProductNames } from './models';
import { Feature } from './features';
import {
  AuthzAny,
  hasPermission as authzHasPermission,
  getPartyIdentifiersForContext,
  Party,
  isAuthzAnyValue,
  Entitlement,
} from '@ascend/authz';
import lzwCompress from 'lzwcompress';

export const identityFn = <T>(t: T): T => t;

export const collectionUtils = {
  groupBy<T, TKey extends string | number, TValue = T>(
    source: T[],
    keySelector: (t: T, index: number) => TKey,
    valueSelector: (t: T, index: number) => TValue,
  ): Record<TKey, TValue[]> {
    return (source || []).reduce((acc, curr, index) => {
      const key = keySelector(curr, index);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(valueSelector(curr, index));
      return acc;
    }, {} as Record<TKey, TValue[]>);
  },
};

export const flatten = <T>(input: T[][]): T[] => ([] as T[]).concat(...input);

export const firstNonEmptyOrDefault = <T>(
  ...args: Array<T[] | undefined>
): T[] => {
  return (
    (args || []).find((a) => {
      const length = a?.length || 0;
      return length > 0;
    }) || []
  );
};

export const buildUrl =
  (rootUrl: string, seperator = '/') =>
  (uriSegment: string) => {
    const normalizedRootUrl = rootUrl.replace(/[/\\]*$/g, '');
    const normalizedUriSegment = uriSegment.replace(/^[/\\]*/g, '');
    return `${normalizedRootUrl}${seperator}${normalizedUriSegment}`;
  };

export const sequencePromiseFunctions =
  <T>(...promises: ((t: T) => Promise<T>)[]) =>
  async (initialValue: T): Promise<T> => {
    let toReturn = initialValue;
    for (const promise of promises) {
      toReturn = await promise(toReturn);
    }
    return toReturn;
  };

export const compressUsingLzw = <T>(input: T): number[] => {
  return lzwCompress.pack(input);
};

export const uncompressLzwObject = (input: number[]): unknown => {
  return lzwCompress.unpack(input);
};

// TODO: (CV) Here we should accomodate/incorporate a party for the permission context (required for non-admin resources)
export const hasPermission = (
  featureCode: string,
  permission: string,
  userProfile: AppUserProfile,
  party?: Party | null,
): boolean => {
  return authzHasPermission(
    featureCode,
    permission,
    party || null,
    userProfile.entitlements,
  );
};

export const getSubCodesForProduct = (
  featureCode: string | undefined | null,
  userProfile: AppUserProfile,
): string[] | AuthzAny => {
  if (!featureCode) {
    return [];
  }

  return getPartyIdentifiersForContext(
    featureCode,
    SubcodePartyType.type,
    userProfile.entitlements,
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyFunctionArgs = (...args: any) => void;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyRecord = Record<string, any>;

export const isAnyProductAdmin = (userProfile: AppUserProfile) => {
  const entitlements = userProfile.entitlements;
  if (isSuperAdmin(userProfile)) {
    return true;
  }
  return (entitlements as Entitlement[]).some((entitlement) => {
    if (isAuthzAnyValue(entitlement.roles)) {
      return true;
    }
  });
};

export const isSuperAdmin = (userProfile: AppUserProfile) => {
  return isAuthzAnyValue(userProfile.entitlements);
};

export const getProductList = (userProfile: AppUserProfile) => {
  if (!userProfile || !userProfile.entitlements) {
    return [];
  }

  const superAdmin = isSuperAdmin(userProfile);
  return superAdmin
    ? ProductNames
    : (userProfile.entitlements as Entitlement[]).map((entl) => entl.context);
};

export const getListOfSupportedFeatures = (
  userProfile: AppUserProfile,
  userFeatures: Feature[],
  adminFeatureCode: string,
): Feature[] => {
  const superAdmin = isSuperAdmin(userProfile);
  if (superAdmin) {
    return userFeatures;
  }
  const productAdmin = isAnyProductAdmin(userProfile);
  const newFeatures = userFeatures.filter(
    (feature) => productAdmin || feature.code !== adminFeatureCode,
  );

  // separate non product features
  const nonProducts = newFeatures.filter(
    (feature) => Object.keys(ProductNameEnum).indexOf(feature.code) === -1,
  );

  // find product features from the user profile entitlements
  const entitlements = (userProfile.entitlements as Entitlement[]) || [];
  const products = newFeatures.filter(
    (feature) =>
      entitlements.findIndex((entl) => entl.context === feature.code) !== -1,
  );
  // add non products and available products
  products.push(...nonProducts);

  return products;
};
